import React, { useState, useMemo, useEffect } from 'react';
import { useMisReservas } from '../hooks/useMisReservas';
import { useAuthCliente } from '../context/AuthClienteContext';
import {
  Box, Heading, VStack, Text, Center, Spinner, Alert, AlertIcon,
  SimpleGrid, Button, HStack, Icon, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure, Link,
  Tooltip, Flex, Spacer, Tag, IconButton
} from '@chakra-ui/react';
import { FaCalendarAlt, FaClock, FaWhatsapp, FaArrowLeft, FaArrowRight, FaUserFriends, FaTrophy, FaTimesCircle, FaCoins, FaUsers } from 'react-icons/fa';
// ✅ IMPORTACIÓN CORREGIDA
import { format, isBefore, differenceInHours } from 'date-fns';
import { es } from 'date-fns/locale/es';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import { useNavigate } from 'react-router-dom';
import { getPhaseName } from '../utils/tournamentLogic';

// --- Sub-componente para la tarjeta de reserva rectangular (Estilo Corregido) ---
function AgendaListItem({ booking, onCancelClick }) {
  const bookingDate = booking.bookingTimestamp ? booking.bookingTimestamp.toDate() : new Date();
  const isCancellable = booking.bookingTimestamp ? differenceInHours(bookingDate, new Date()) >= 8 : false;

  return (
    <Flex 
      p={4} 
      bg="gray.50"
      borderRadius="lg" 
      w="full" 
      align="center"
      borderWidth="1px"
      borderColor="gray.200"
      _hover={{ bg: 'gray.100', transform: 'scale(1.02)', transition: 'transform 0.2s' }}
      transition="transform 0.2s"
    >
      <HStack spacing={4} flex="1">
        <VStack bg="green.50" color="green.700" p={3} borderRadius="md" w="70px" h="70px" justifyContent="center">
          <Text fontSize="md" fontWeight="bold" textTransform="uppercase">{format(bookingDate, "EEE", { locale: es })}</Text>
          <Text fontSize="3xl" fontWeight="bold" lineHeight="1">{format(bookingDate, "d")}</Text>
        </VStack>
        <VStack align="start" spacing={1} flex="1">
          <Heading size="md" color="brand.700">{format(bookingDate, "HH:mm'h'", { locale: es })}</Heading>
          <Text fontSize="sm" color="gray.600" textTransform="capitalize">{format(bookingDate, "d 'de' MMMM, yyyy", { locale: es })}</Text>
          <Text fontSize="sm" color="gray.600">Cancha: {booking.resourceName}</Text>
        </VStack>
      </HStack>
      <Spacer />
      <Tooltip 
        label={isCancellable ? "Cancelar reserva" : "No se puede cancelar con menos de 8 horas de antelación"}
        hasArrow
      >
        <IconButton
          icon={<FaTimesCircle />}
          size="sm"
          isRound
          colorScheme="red"
          variant="ghost"
          aria-label="Cancelar reserva"
          onClick={() => onCancelClick(booking)}
          isDisabled={!isCancellable}
        />
      </Tooltip>
    </Flex>
  );
}

function TokenLogItem({ log }) {
  return (
    <Flex p={3} borderRadius="lg" borderWidth="1px" borderColor="gray.200" align="center" bg="gray.50" w="full">
      <Icon as={FaCoins} color="yellow.500" w={6} h={6} mr={4} />
      <VStack align="start" spacing={0}>
        <Text fontWeight="bold">+{log.amount} Tokens</Text>
        <Text fontSize="xs" color="gray.500">Abonados por {log.operatorName} - {log.createdAt ? format(log.createdAt.toDate(), "d MMM, yyyy", { locale: es }) : 'Fecha no disponible'}</Text>
      </VStack>
    </Flex>
  );
}

function PaginationControls({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  return (
    <HStack>
      <IconButton size="sm" icon={<FaArrowLeft />} aria-label="Página anterior" isDisabled={currentPage === 1} onClick={() => onPageChange(p => p - 1)} />
      <Text fontSize="sm">Pág {currentPage} de {totalPages || 1}</Text>
      <IconButton size="sm" icon={<FaArrowRight />} aria-label="Página siguiente" isDisabled={currentPage >= totalPages} onClick={() => onPageChange(p => p + 1)} />
    </HStack>
  );
}

function useTokenLogs(companyId, userId) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { db } = useAuthCliente();

  useEffect(() => {
    if (!db || !companyId || !userId) {
      setLoading(false);
      return;
    }
    const logsRef = collection(db, 'companies', companyId, 'end_customers', userId, 'token_logs');
    const q = query(logsRef, orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const logsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setLogs(logsData);
        setLoading(false);
      }, 
      (err) => {
        console.error("Error fetching token logs:", err);
        setError("No se pudo cargar el historial de tokens.");
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [db, companyId, userId]);

  return { logs, loading, error };
}

function useMyTournaments(companyId, userId) {
  const [myTournaments, setMyTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const { db } = useAuthCliente();

  useEffect(() => {
    if (!db || !companyId || !userId) {
      setLoading(false);
      return;
    }
    const tournamentsRef = collection(db, 'companies', companyId, 'tournaments');
    const q = query(tournamentsRef, where('participants', 'array-contains', userId));
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const tournamentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMyTournaments(tournamentsData);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching user's tournaments:", error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [db, companyId, userId]);
  return { myTournaments, loading };
}

function MyTournamentCard({ tournament }) {
  const navigate = useNavigate();
  const statusColors = { open: 'green', closed: 'yellow', 'in-progress': 'blue', };
  return (
    <VStack p={4} bg="white" borderRadius="lg" w="full" align="start" spacing={3} borderWidth="1px" borderColor="gray.200" transition="all 0.2s" _hover={{ boxShadow: 'md' }}>
      <HStack w="full" justify="space-between">
        <Heading size="sm">{tournament.name}</Heading>
        <Tag size="sm" colorScheme={statusColors[tournament.status] || 'gray'}>
          {tournament.status === 'open' ? 'Inscripciones Abiertas' : 'En Progreso'}
        </Tag>
      </HStack>
      <Text fontSize="sm" color="gray.500">{tournament.dates}</Text>
      <HStack w="full" fontSize="sm" color="gray.600">
        <Icon as={FaUsers} />
        <Text>{tournament.participants?.length || 0} inscritos</Text>
      </HStack>
      <Button size="sm" w="full" variant="outline" colorScheme="brand" onClick={() => navigate('/portal/torneos')}>
        Ver Torneo
      </Button>
    </VStack>
  );
}

function useUpcomingMatches(companyId, userId) {
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const { db } = useAuthCliente();

  useEffect(() => {
    if (!db || !companyId || !userId) {
      setLoading(false);
      return;
    }

    const tournamentsRef = collection(db, 'companies', companyId, 'tournaments');
    const q = query(
      tournamentsRef, 
      where('participants', 'array-contains', userId),
      where('status', '==', 'in-progress')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allMatches = [];
      snapshot.docs.forEach(doc => {
        const tournament = { id: doc.id, ...doc.data() };
        const rounds = tournament.rounds || {};

        Object.entries(rounds).forEach(([roundIndex, roundMatches]) => {
          roundMatches.forEach((match, matchIndex) => {
            const isUserInMatch = match.player1?.id === userId || match.player2?.id === userId;
            const hasResult = tournament.results?.[roundIndex]?.[matchIndex];

            if (isUserInMatch && match.player1 && match.player2 && !hasResult) {
              const opponent = match.player1.id === userId ? match.player2 : match.player1;
              // Buscamos la fecha del partido en el documento del torneo
              const matchDate = tournament.matchDates?.[roundIndex]?.[matchIndex]?.toDate() || null;
              
              allMatches.push({
                id: `${tournament.id}-${roundIndex}-${matchIndex}`,
                tournamentName: tournament.name,
                tournamentId: tournament.id,
                phaseName: getPhaseName(roundIndex, tournament.participants.length),
                opponent: opponent,
                date: matchDate, // <-- La añadimos aquí
              });
            }
          });
        });
      });
      setUpcomingMatches(allMatches);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db, companyId, userId]);
  return { upcomingMatches, loading };
}

function UpcomingMatchCard({ match }) {
    const navigate = useNavigate();
    return (
        <VStack 
            p={4} 
            bg="white" 
            borderRadius="lg" 
            w="full" 
            align="start" 
            spacing={3}
            borderWidth="1px"
            borderColor="gray.200"
        >
            <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">{match.tournamentName}</Text>
            <HStack w="full" justify="space-between">
                <VStack align="start" spacing={0}>
                    <Text fontWeight="bold">Tu Próximo Rival:</Text>
                    <Text fontSize="lg" color="brand.700">{match.opponent.name}</Text>
                </VStack>
                <Tag colorScheme="blue">{match.phaseName}</Tag>
            </HStack>
            {/* Mostramos la fecha si existe */}
            {match.date ? (
              <HStack spacing={2} fontSize="sm" color="gray.600">
                <Icon as={FaCalendarAlt} />
                <Text>{format(match.date, "EEEE, d 'de' MMMM", { locale: es })}</Text>
                <Icon as={FaClock} />
                <Text fontWeight="bold">{format(match.date, "HH:mm'h'")}</Text>
              </HStack>
            ) : (
              <Tag size="sm">Fecha por confirmar</Tag>
            )}
            <Button size="sm" w="full" onClick={() => navigate('/portal/torneos')}>
                Ir al Torneo para ver el cuadro
            </Button>
        </VStack>
    );
}

export default function MisReservasPage() {
  const { bookings, loading: loadingBookings, error: errorBookings } = useMisReservas();
  const { currentUser, cancelBooking } = useAuthCliente();
  const toast = useToast();
  
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const { isOpen: isInfoOpen, onOpen: onInfoOpen, onClose: onInfoClose } = useDisclosure();

  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  
  // --- Estados de Paginación ---
  const [agendaPage, setAgendaPage] = useState(1);
  const [tokenLogPage, setTokenLogPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [myTournamentsPage, setMyTournamentsPage] = useState(1);
  const [upcomingMatchesPage, setUpcomingMatchesPage] = useState(1);

  // --- Constantes de Paginación ---
  const itemsPerPage = 5;
  const matchesPerPage = 3;
  const TOURNAMENTS_PER_PAGE = 3; // ✅ Constante re-declarada

  // --- Hooks de datos ---
  const { logs: tokenLogs, loading: loadingTokenLogs } = useTokenLogs(currentUser?.companyId, currentUser?.uid);
  const { myTournaments, loading: loadingMyTournaments } = useMyTournaments(currentUser?.companyId, currentUser?.uid);
  const { upcomingMatches, loading: loadingUpcomingMatches } = useUpcomingMatches(currentUser?.companyId, currentUser?.uid);

const { upcomingBookings, pastBookings } = useMemo(() => {
    const upcoming = [];
    const past = [];
    const now = new Date();
    bookings.forEach(booking => {
      if (booking.bookingTimestamp && booking.bookingTimestamp.toDate) {
        const bookingDate = booking.bookingTimestamp.toDate();
        if (isBefore(bookingDate, now)) {
          past.push(booking);
        } else {
          upcoming.push(booking);
        }
      } else {
        upcoming.push(booking);
      }
    });
    upcoming.sort((a, b) => a.bookingTimestamp.toDate() - b.bookingTimestamp.toDate());
    past.sort((a, b) => b.bookingTimestamp.toDate() - a.bookingTimestamp.toDate());
    return { upcomingBookings: upcoming, pastBookings: past };
  }, [bookings]);

  const totalAgendaPages = Math.ceil(upcomingBookings.length / itemsPerPage);
  const paginatedUpcomingBookings = upcomingBookings.slice((agendaPage - 1) * itemsPerPage, agendaPage * itemsPerPage);

  const totalTokenLogPages = Math.ceil(tokenLogs.length / itemsPerPage);
  const paginatedTokenLogs = tokenLogs.slice((tokenLogPage - 1) * itemsPerPage, tokenLogPage * itemsPerPage);

  const totalHistoryPages = Math.ceil(pastBookings.length / itemsPerPage);
  const paginatedPastBookings = pastBookings.slice((historyPage - 1) * itemsPerPage, historyPage * itemsPerPage);
  
  const totalMyTournamentsPages = Math.ceil(myTournaments.length / TOURNAMENTS_PER_PAGE);
  const paginatedMyTournaments = myTournaments.slice((myTournamentsPage - 1) * TOURNAMENTS_PER_PAGE, myTournamentsPage * TOURNAMENTS_PER_PAGE);

  const totalUpcomingMatchesPages = Math.ceil(upcomingMatches.length / matchesPerPage);
  const paginatedUpcomingMatches = upcomingMatches.slice((upcomingMatchesPage - 1) * matchesPerPage, upcomingMatchesPage * matchesPerPage);

  const handleCancelClick = (booking) => {
    if (!booking.bookingTimestamp) {
      toast({ title: "No se puede cancelar", status: "warning", isClosable: true });
      return;
    }
    const hoursDifference = differenceInHours(booking.bookingTimestamp.toDate(), new Date());
    if (hoursDifference < 8) {
      onInfoOpen();
    } else {
      setBookingToCancel(booking);
      onConfirmOpen();
    }
  };

  const handleConfirmCancel = async () => {
    if (!bookingToCancel) return;
    setIsCancelling(true);
    try {
      await cancelBooking(bookingToCancel.id);
      toast({ title: "Reserva Cancelada", description: "Tu token ha sido reembolsado.", status: "success" });
      onConfirmClose();
    } catch (err) {
      toast({ title: "Error al cancelar", description: err.message, status: "error" });
    } finally {
      setIsCancelling(false);
    }
  };

  if (loadingBookings || loadingMyTournaments || loadingUpcomingMatches) return <Center p={10}><Spinner size="xl" /></Center>;
  if (errorBookings) return <Alert status="error"><AlertIcon />{errorBookings}</Alert>;

  return (
    <>
      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} isCentered>
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader>Confirmar Cancelación</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>¿Estás seguro de que deseas cancelar tu reserva para la <strong>{bookingToCancel?.resourceName}</strong> el día <strong>{bookingToCancel && format(bookingToCancel.bookingTimestamp.toDate(), 'd/MM/yyyy')} a las {bookingToCancel?.startTime}</strong>?</Text>
            <Text mt={4} fontWeight="bold">Se te reembolsará 1 token de reserva.</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="green" mr={3} onClick={onConfirmClose}>No, mantener</Button>
            <Button colorScheme="red" onClick={handleConfirmCancel} isLoading={isCancelling}>Sí, cancelar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isInfoOpen} onClose={onInfoClose} isCentered>
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader>Cancelación no Permitida</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>Las cancelaciones solo se permiten con más de 8 horas de antelación.</Text>
            <Text mt={4}>Por favor, ponte en contacto con la administración del club para solicitar una cancelación manual.</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onInfoClose}>Entendido</Button>
            {currentUser?.contactPhone && (
              <Link href={`https://wa.me/${currentUser.contactPhone.replace(/\D/g, '')}`} isExternal>
                <Button colorScheme="whatsapp" leftIcon={<FaWhatsapp />}>Contactar por WhatsApp</Button>
              </Link>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <VStack spacing={8} align="stretch">
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} alignItems="start">
          
          <VStack spacing={8} align="stretch">
            <Box bg="white" p={5} borderRadius="xl" boxShadow="lg">
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="lg" >Mis Próximas Reservas</Heading>
                <PaginationControls currentPage={agendaPage} totalPages={totalAgendaPages} onPageChange={setAgendaPage} />
              </Flex>
              {paginatedUpcomingBookings.length > 0 ? (
                <VStack spacing={3}>
                  {paginatedUpcomingBookings.map(booking => (
                    <AgendaListItem key={booking.id} booking={booking} onCancelClick={handleCancelClick} />
                  ))}
                </VStack>
              ) : (
                <Center p={10} bg="gray.50" borderRadius="md"><Text color="gray.500">No tienes reservas próximas.</Text></Center>
              )}
            </Box>

            <Box bg="white" p={5} borderRadius="xl" boxShadow="lg">
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="lg">Mis Siguientes Partidos</Heading>
                <PaginationControls 
                    currentPage={upcomingMatchesPage}
                    totalPages={totalUpcomingMatchesPages}
                    onPageChange={setUpcomingMatchesPage}
                />
              </Flex>
              {loadingUpcomingMatches ? <Center p={10}><Spinner /></Center> :
                paginatedUpcomingMatches.length > 0 ? (
                    <VStack spacing={3}>
                        {paginatedUpcomingMatches.map(match => <UpcomingMatchCard key={match.id} match={match} />)}
                    </VStack>
                ) : (
                    <Center p={10} bg="gray.50" borderRadius="md"><Text color="gray.500">No tienes partidos de torneo programados por ahora.</Text></Center>
                )
              }
            </Box>

            <Box bg="white" p={5} borderRadius="xl" boxShadow="lg">
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="lg">Torneos En Los Que Participo</Heading>
                <PaginationControls 
                  currentPage={myTournamentsPage} 
                  totalPages={totalMyTournamentsPages} 
                  onPageChange={setMyTournamentsPage} 
                />
              </Flex>
              {myTournaments.length > 0 ? (
                <VStack spacing={3}>
                  {paginatedMyTournaments.map(tournament => (
                    <MyTournamentCard key={tournament.id} tournament={tournament} />
                  ))}
                </VStack>
              ) : (
                <Center p={10} bg="gray.50" borderRadius="md"><Text color="gray.500">No estás inscrito en ningún torneo actualmente.</Text></Center>
              )}
            </Box>

            <Box bg="white" p={5} borderRadius="xl" boxShadow="lg">
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="lg">Torneos En Los Que Participo</Heading>
                <PaginationControls 
                  currentPage={myTournamentsPage} 
                  totalPages={totalMyTournamentsPages} 
                  onPageChange={setMyTournamentsPage} 
                />
              </Flex>
              {myTournaments.length > 0 ? (
                <VStack spacing={3}>
                  {paginatedMyTournaments.map(tournament => (
                    <MyTournamentCard key={tournament.id} tournament={tournament} />
                  ))}
                </VStack>
              ) : (
                <Center p={10} bg="gray.50" borderRadius="md"><Text color="gray.500">No estás inscrito en ningún torneo actualmente.</Text></Center>
              )}
            </Box>

          </VStack>

          <VStack spacing={8} align="stretch">
            <Box bg="white" p={5} borderRadius="xl" boxShadow="lg" >
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="lg">Historial de Reservas</Heading>
                <PaginationControls currentPage={historyPage} totalPages={totalHistoryPages} onPageChange={setHistoryPage} />
              </Flex>
              {paginatedPastBookings.length > 0 ? (
                <VStack spacing={3}>
                  {paginatedPastBookings.map(booking => (
                    <Flex key={booking.id} p={3} w="full" borderRadius="lg" borderWidth="1px" borderColor="gray.200" align="center" bg="gray.50">
                      <VStack align="start">
                        <Text fontWeight="bold">{booking.resourceName}</Text>
                        <Text fontSize="sm" color="gray.600">{booking.bookingTimestamp ? format(booking.bookingTimestamp.toDate(), "EEEE, d MMM yyyy 'a las' HH:mm'h'", { locale: es }) : 'Fecha inválida'}</Text>
                      </VStack>
                    </Flex>
                  ))}
                </VStack>
              ) : (
                <Text color="gray.500">Aún no tienes reservas en tu historial.</Text>
              )}
            </Box>

            <Box bg="white" p={5} borderRadius="xl" boxShadow="lg">
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="lg">Registro de Tokens</Heading>
                <PaginationControls currentPage={tokenLogPage} totalPages={totalTokenLogPages} onPageChange={setTokenLogPage} />
              </Flex>
              {loadingTokenLogs ? <Center p={10}><Spinner /></Center> :
               paginatedTokenLogs.length > 0 ? (
                <VStack spacing={3}>
                  {paginatedTokenLogs.map(log => (
                    <TokenLogItem key={log.id} log={log} />
                  ))}
                </VStack>
              ) : (
                <Center p={10} bg="gray.50" borderRadius="md"><Text color="gray.500">No hay registros de abonos de tokens.</Text></Center>
              )}
            </Box>
          </VStack>

        </SimpleGrid>
      </VStack>
    </>
  );
}